import { DiscountCalculatorFactory } from './DiscountCalculator';
import type {
  DiscountBxgy,
  DiscountFixedAmount,
  DiscountPercentage,
} from '@/modules/store/models/Discount';
import type { Amount, CalculatedPrice, Price } from '@/modules/store/models/Price';
import type { Product } from '@/modules/store/models/Product';
import type { Promotion } from '@/modules/store/models/Promotion';
import { roundPayableAmount, roundUnitPrice } from '@/utils/currency/roundPrice';

interface Sku {
  prices: Price[];
  promotions: Promotion[];
}

export interface ProductPriceItem {
  prices?: Price[];
  promotions?: Promotion[];
  product?: Product;
  sku?: Sku;
}

class ProductPriceService {
  calculateUnitPrice(item: ProductPriceItem): CalculatedPrice | undefined {
    return this.calculatePrice(item.prices, 1, item.promotions);
  }

  calculateLineItemPrice(item: ProductPriceItem, quantity: number): CalculatedPrice | undefined {
    return this.calculatePrice(item.prices, quantity, item.promotions);
  }

  private calculatePrice(
    prices?: Price[],
    quantity?: number,
    promotion?: Promotion | Promotion[],
  ): CalculatedPrice | undefined {
    if (!prices?.length || quantity === undefined || isNaN(quantity)) {
      return;
    }

    const price = this.getPriceForQuantity(prices, quantity);
    if (!price?.price || price.price.amount < 0) {
      return;
    }

    const normalizedPromotion = Array.isArray(promotion)
      ? this.getPromotionForQuantity(promotion, quantity)
      : promotion;

    const discounted = normalizedPromotion
      ? this.calculateDiscountedPrice(price, quantity, normalizedPromotion)
      : undefined;

    if (discounted) {
      return discounted;
    }

    const originalPrice: Amount = {
      ...price.price,
      amount: roundPayableAmount(quantity * roundUnitPrice(price.price.amount)),
    };

    return {
      ...originalPrice,
      originalPrice,
      promotion: normalizedPromotion,
    };
  }

  private calculateDiscountedPrice(
    originalPrice: Price,
    quantity: number,
    promotion: Promotion,
  ): CalculatedPrice | undefined {
    const originalAmount = originalPrice.price;

    const { discountedPrice, discountFixedAmount, discountPercentage, discountBxgy } =
      promotion.discounts.reduce<{
        discountedPrice?: Amount;
        discountFixedAmount?: DiscountFixedAmount;
        discountPercentage?: DiscountPercentage;
        discountBxgy?: DiscountBxgy;
      }>((acc, discount) => {
        if (acc.discountedPrice) return acc;

        const calculator = DiscountCalculatorFactory.create(discount);

        if (!calculator.isApplicable(quantity, discount)) return acc;

        const result = calculator.calculate(originalPrice, quantity, discount);

        if (!result) return acc;

        return {
          discountedPrice: result.discountedPrice,
          discountFixedAmount: discount.discountType === 'fixedAmount' ? discount : undefined,
          discountPercentage: discount.discountType === 'percentage' ? discount : undefined,
          discountBxgy: discount.discountType === 'bxgy' ? discount : undefined,
        };
      }, {});

    return discountedPrice
      ? {
          ...discountedPrice,
          originalPrice: {
            ...originalAmount,
            amount: roundPayableAmount(quantity * roundUnitPrice(originalAmount.amount)),
          },
          discountedPrice,
          promotion,
          discountFixedAmount,
          discountPercentage,
          discountBxgy,
        }
      : undefined;
  }

  /**
   * Returns the first price that matches the given quantity.
   */
  private getPriceForQuantity(prices: Price[], quantity: number): Price | undefined {
    return prices.find((p) => {
      if (
        p.quantityFrom !== undefined &&
        !isNaN(p.quantityFrom) &&
        p.quantityTo !== undefined &&
        !isNaN(p.quantityTo)
      ) {
        return quantity >= p.quantityFrom && quantity <= p.quantityTo;
      }

      return true;
    });
  }

  /**
   * Finds the earliest promotion that applies based on the given quantity.
   */
  private getPromotionForQuantity(
    promotions: Promotion[],
    quantity: number,
  ): Promotion | undefined {
    const validPromotions = promotions.filter((p) => p.minimumQuantity <= quantity);

    if (validPromotions.length === 0) {
      return;
    }

    return validPromotions.reduce((earliest, current) =>
      current.createdAt < earliest.createdAt ? current : earliest,
    );
  }
}

export default ProductPriceService;
